/**
 * Catalog of integration kinds (ADR-0085 Phase 2 §5.5).
 *
 * Each kind registers its sub-form component + the operation-graph node type
 * it unlocks + display strings. The generic Integrations admin pages and the
 * operation-editor palette read from this catalog. Adding a future kind
 * (FRED / USITC / AI keys / ...) is: add a sub-form component, append an
 * entry here, run.
 */

import type { ComponentType } from "react";
import { AiProviderIntegrationForm } from "../components/integrations/AiProviderIntegrationForm.js";
import { MicrosoftIntegrationForm } from "../components/integrations/MicrosoftIntegrationForm.js";
import { PlexIntegrationForm } from "../components/integrations/PlexIntegrationForm.js";
import type { IntegrationFormProps, TenantIntegrationKind } from "../components/integrations/types.js";

export type { TenantIntegrationKind } from "../components/integrations/types.js";

export interface IntegrationKindMeta {
  kind: TenantIntegrationKind;
  label: string;
  description: string;
  /** Short copy shown next to the kind picker / on the list. */
  helpText: string;
  /** Longer paragraph shown atop the New form. */
  introCopy: string;
  FormComponent: ComponentType<IntegrationFormProps>;
  /** The operation-graph node type this kind unlocks. */
  nodeTypeForOperationGraph: string;
}

export const INTEGRATION_KINDS: Record<TenantIntegrationKind, IntegrationKindMeta> = {
  plex_api: {
    kind: "plex_api",
    label: "Plex API",
    description: "Plex Cloud Data Source API integration.",
    helpText: "Adds a Plex API node to your operation graph palette.",
    introCopy:
      "These credentials let Atlas call Plex's Data Source API on your behalf. You'll need an " +
      "API service account from Plex Customer Care — your regular Plex login is not used here. " +
      "Once saved, a Plex API node becomes available in your operation graphs.",
    FormComponent: PlexIntegrationForm,
    nodeTypeForOperationGraph: "plex_api"
  },
  microsoft_graph: {
    kind: "microsoft_graph",
    label: "Microsoft 365",
    description: "Mirror finished study documents into SharePoint and OneDrive.",
    helpText: "Projects finished documents into your SharePoint libraries. Atlas stays the system of record.",
    introCopy:
      "This connects Atlas to your organization's Microsoft 365 so finished study documents can be mirrored " +
      "into SharePoint libraries your team already reads. Your Microsoft 365 administrator approves the access — " +
      "Atlas gives you a complete decision package to send them. No credentials are entered here.",
    FormComponent: MicrosoftIntegrationForm,
    // Mirroring is not an operation-graph capability — no node type unlocks.
    nodeTypeForOperationGraph: ""
  },
  // ADR-0207 §3.1: the workspace's own AI provider. At most one per workspace;
  // the name is server-fixed.
  ai_provider: {
    kind: "ai_provider",
    label: "AI provider",
    description: "The AI service your workspace's Atlas features run on.",
    helpText: "Atlas's AI features call your provider with your credentials. Nothing runs on Atlas's account.",
    introCopy:
      "Connect the AI service your organization has approved. Atlas's in-app AI — chat assistants, the operation " +
      "architect, knowledge capture — sends its requests to this provider using the credential you enter here, and " +
      "your provider bills you directly. Your provider's data terms govern those requests.",
    FormComponent: AiProviderIntegrationForm,
    // Not an operation-graph capability — no node type unlocks.
    nodeTypeForOperationGraph: ""
  }
};

export const INTEGRATION_KIND_LIST: IntegrationKindMeta[] = Object.values(INTEGRATION_KINDS);
